import { useSyncExternalStore } from 'react'

/**
 * Viewport breakpoints shared by the components and the stylesheets (#109).
 *
 * The same numbers live in `styles/responsive.css`; a component that decides its markup by
 * a different width than the stylesheet decides its layout would show, for a band of widths,
 * a drawer button with no drawer or table cards squeezed into the desktop grid.
 */
export const NAV_DRAWER_QUERY = '(max-width: 860px)'
/** Below this width a list renders its rows as cards instead of a table. */
export const LIST_CARDS_QUERY = '(max-width: 640px)'

// One subscriber function per query: a fresh function on every render would make React
// unsubscribe and subscribe again each time.
const subscribers = new Map<string, (onChange: () => void) => () => void>()

function subscribe(query: string) {
  const known = subscribers.get(query)
  if (known) return known
  const created = (onChange: () => void) => {
    const media = typeof window.matchMedia === 'function' ? window.matchMedia(query) : undefined
    if (!media) return () => {}
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }
  subscribers.set(query, created)
  return created
}

/**
 * Whether the media query matches now, re-rendering when it stops or starts matching.
 *
 * Without `matchMedia` (jsdom, very old engines) the answer is "no": the desktop layout is
 * the one every existing screen was written and tested against, so it is the safe default.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribe(query),
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    () => false,
  )
}
